import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { ApiError } from '../api/client';
import {
  createCustomer,
  getCustomer,
  updateCustomer,
  type CustomerCreateRequest,
  type CustomerRecord,
} from '../api/customers';

/**
 * Unified create + edit page for a Customer.
 *
 * Mode is driven off the URL:
 *   - `/customers/new` → create mode (no fetch, empty form).
 *   - `/customers/:id`  → edit mode (fetch + populate).
 *
 * Why combined rather than two components: the form markup, validation rules,
 * and success-path navigation are identical. Splitting into `<CreatePage />`
 * and `<EditPage />` would duplicate ~200 lines of form wiring for the sake of
 * a single branch on `id`.
 *
 * 409 duplicate-name handling: the server returns RFC 7807
 * `application/problem+json` with `type: '/errors/conflict'` on a duplicate
 * name. We catch it on the mutation and surface `detail` inline next to the
 * name field, NOT as a global toast — a global toast would pull the user away
 * from the actual broken field.
 */

// Matches the server's CustomerCreateRequest surface, with client-only
// validation text. We keep the schema local so the error copy is tailored to
// the UI rather than reusing the terse server messages.
const FormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required.')
    .max(200, 'Name must be 200 characters or fewer.'),
  addressLine1: z.string().trim().max(500).optional(),
  addressLine2: z.string().trim().max(500).optional(),
  city: z.string().trim().max(200).optional(),
  region: z.string().trim().max(200).optional(),
  postcode: z.string().trim().max(50).optional(),
  billingContactName: z.string().trim().max(200).optional(),
  billingEmail: z
    .string()
    .trim()
    .max(320)
    .optional()
    .refine((v) => v === undefined || v === '' || z.string().email().safeParse(v).success, {
      message: 'Enter a valid email address.',
    }),
});

type FormValues = {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postcode: string;
  billingContactName: string;
  billingEmail: string;
};

const EMPTY_FORM: FormValues = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postcode: '',
  billingContactName: '',
  billingEmail: '',
};

function fromRecord(record: CustomerRecord): FormValues {
  return {
    name: record.name,
    addressLine1: record.addressLine1 ?? '',
    addressLine2: record.addressLine2 ?? '',
    city: record.city ?? '',
    region: record.region ?? '',
    postcode: record.postcode ?? '',
    billingContactName: record.billingContactName ?? '',
    billingEmail: record.billingEmail ?? '',
  };
}

/**
 * Strip empty strings so we do not push `''` into nullable columns on the
 * server. `undefined` drops the field from the JSON payload entirely, which
 * the server treats as "no change" on PATCH and "not provided" on POST.
 */
function toPayload(values: FormValues): CustomerCreateRequest {
  const payload: CustomerCreateRequest = { name: values.name.trim() };
  const optional: Array<keyof Omit<FormValues, 'name'>> = [
    'addressLine1',
    'addressLine2',
    'city',
    'region',
    'postcode',
    'billingContactName',
    'billingEmail',
  ];
  for (const key of optional) {
    const raw = values[key].trim();
    if (raw.length > 0) {
      (payload as Record<string, string>)[key] = raw;
    }
  }
  return payload;
}

type FieldErrors = Partial<Record<keyof FormValues, string>>;

export function CustomerEditPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Route is `/customers/new` OR `/customers/:id`. In both cases `params.id`
  // is populated by react-router; we use the literal string 'new' as the
  // sentinel for create mode.
  const rawId = params.id;
  const isCreate = rawId === undefined || rawId === 'new';
  const customerId = isCreate ? null : rawId;

  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const editQuery = useQuery({
    queryKey: ['customers', 'detail', customerId] as const,
    queryFn: () => {
      if (customerId === null) throw new Error('unreachable: edit query enabled without id');
      return getCustomer(customerId);
    },
    enabled: customerId !== null,
  });

  // Populate the form once the record arrives. We only do this on the first
  // successful load so subsequent refetches do not clobber in-progress edits.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!hydrated && editQuery.data) {
      setValues(fromRecord(editQuery.data));
      setHydrated(true);
    }
  }, [editQuery.data, hydrated]);

  const createMutation = useMutation({
    mutationFn: (input: CustomerCreateRequest) => createCustomer(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate('/customers');
    },
    onError: (err) => handleMutationError(err),
  });

  const updateMutation = useMutation({
    mutationFn: (input: CustomerCreateRequest) => {
      if (customerId === null) throw new Error('unreachable: update without id');
      return updateCustomer(customerId, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate('/customers');
    },
    onError: (err) => handleMutationError(err),
  });

  function handleMutationError(err: unknown): void {
    if (err instanceof ApiError && err.type === '/errors/conflict') {
      // Treat 409 as a duplicate-name field-level error. Server-side the only
      // 409 path on this resource is the unique-name constraint.
      setErrors((prev) => ({ ...prev, name: err.detail }));
      setFormError(null);
      return;
    }
    if (err instanceof Error) {
      setFormError(err.message);
      return;
    }
    setFormError('Save failed.');
  }

  function updateField<K extends keyof FormValues>(key: K, value: FormValues[K]): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Clear the field-level error as soon as the user edits it, so stale 409
    // copy does not linger while they fix the value.
    setErrors((prev) => {
      if (prev[key] === undefined) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);

    const parsed = FormSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !(key in fieldErrors)) {
          fieldErrors[key as keyof FormValues] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    const payload = toPayload(parsed.data as FormValues);

    if (isCreate) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate(payload);
    }
  }

  function handleCancel(): void {
    navigate('/customers');
  }

  const pending = createMutation.isPending || updateMutation.isPending;

  if (editQuery.isLoading) {
    return (
      <section aria-label="Customer">
        <div role="status" aria-live="polite" aria-busy="true">
          Loading customer…
        </div>
      </section>
    );
  }

  if (editQuery.isError) {
    return (
      <section aria-label="Customer">
        <div role="alert" className="text-red-700">
          Failed to load customer:{' '}
          {editQuery.error instanceof Error ? editQuery.error.message : 'unknown'}
        </div>
      </section>
    );
  }

  return (
    <section aria-label={isCreate ? 'New customer' : 'Edit customer'} className="space-y-4">
      <h1 className="text-2xl font-semibold">{isCreate ? 'New customer' : 'Edit customer'}</h1>

      {formError !== null && (
        <div role="alert" className="text-red-700">
          {formError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4 max-w-xl">
        <Field
          id="name"
          label="Name"
          required
          value={values.name}
          onChange={(v) => updateField('name', v)}
          error={errors.name}
        />
        <Field
          id="addressLine1"
          label="Address line 1"
          value={values.addressLine1}
          onChange={(v) => updateField('addressLine1', v)}
          error={errors.addressLine1}
        />
        <Field
          id="addressLine2"
          label="Address line 2"
          value={values.addressLine2}
          onChange={(v) => updateField('addressLine2', v)}
          error={errors.addressLine2}
        />
        <Field
          id="city"
          label="City"
          value={values.city}
          onChange={(v) => updateField('city', v)}
          error={errors.city}
        />
        <Field
          id="region"
          label="Region"
          value={values.region}
          onChange={(v) => updateField('region', v)}
          error={errors.region}
        />
        <Field
          id="postcode"
          label="Postcode"
          value={values.postcode}
          onChange={(v) => updateField('postcode', v)}
          error={errors.postcode}
        />
        <Field
          id="billingContactName"
          label="Billing contact name"
          value={values.billingContactName}
          onChange={(v) => updateField('billingContactName', v)}
          error={errors.billingContactName}
        />
        <Field
          id="billingEmail"
          label="Billing email"
          type="email"
          value={values.billingEmail}
          onChange={(v) => updateField('billingEmail', v)}
          error={errors.billingEmail}
        />

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={pending}
            className="bg-brand-teal text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {pending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={pending}
            className="px-4 py-2 border rounded disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * Small local Field component. Not extracted to a shared module because it is
 * used in exactly one place; promoting it would be premature. If a second
 * page needs the same pattern (label + input + error slot), lift it then.
 */
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  required?: boolean;
  type?: 'text' | 'email';
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  required = false,
  type = 'text',
}: FieldProps): JSX.Element {
  const errorId = `${id}-error`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium">
        {label}
        {required && (
          <span aria-hidden="true" className="text-red-700">
            {' '}
            *
          </span>
        )}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="border rounded px-2 py-1"
      />
      {error !== undefined && (
        <span id={errorId} role="alert" className="text-sm text-red-700">
          {error}
        </span>
      )}
    </div>
  );
}
